import {Box, Text, LazyImage} from '@miro-site/core'
import React, {useEffect, useState, useRef} from 'react'
import styled from 'styled-components'
import type {SearchResult} from '../Search'
import SearchResultItem from './SearchResultItem'

export type StatusMessages = {
  SEARCHING: string
  TYPE_MORE: string
  NO_RESULT: string
}

export interface SearchDropdownProps {
  searchString: string
  open: boolean
  searchResult?: SearchResult[]
  isSearching?: boolean
  onSelectTemplate?: (template: SearchResult) => void
  statusMessages: StatusMessages
  activeIndex: number
  onItemHover?: (itemIndexHover: number) => void
}

export interface ImageProps {
  src: string
}

const Wrapper = styled.div`
  border-top: 1px solid #e1e0e7;
  display: flex;
  flex-direction: row;

  ${({theme}) =>
    theme.normalizeStyles({
      paddingLeft: [null, 26, 26, 26],
      paddingRight: [null, 26, 26, 26],
    })}
`

const List = styled(Box).attrs({
  as: 'ul',
})`
  width: 100%;
  overflow-y: auto;

  ${({theme}) =>
    theme.normalizeStyles({
      paddingBottom: 18,
      paddingRight: [null, 52],
      paddingTop: 18,
      borderRight: ['none', '1px solid #e1e0e7'],
      maxHeight: 288,
    })}
`

const Preview = styled.a`
  ${({theme}) =>
    theme.normalizeStyles({
      paddingBottom: 40,
      paddingLeft: 60,
      paddingTop: 40,
      width: 388,
      display: ['none', 'block', 'block', 'block'],
    })}

  &:hover {
    ${Text} {
      transition: color ${({theme}) => theme.animation.transitionDuration};
      color: ${({theme}) => theme.colors.primaryBlueHover};
    }
  }
`

const NoResult = styled.div`
  color: #827f9b;

  ${({theme}) =>
    theme.normalizeStyles({
      paddingLeft: [10, 34, 34, 34],
      paddingRight: [10, 34, 34, 34],
      paddingTop: 24,
      paddingBottom: 24,
      fontSize: 18,
      lineHeight: 20,
    })}
`

const SearchDropdown: React.FC<SearchDropdownProps> = ({
  searchString = '',
  searchResult = [],
  onSelectTemplate,
  isSearching,
  statusMessages,
  activeIndex,
  onItemHover,
  ...props
}) => {
  const [selectedItem, setSelectedItem] = useState<number>(0)

  const list = useRef<HTMLDivElement>(null)
  const listItem = useRef<HTMLLIElement>(null)

  const scrollToTop = () => {
    const elHeight: number =
      typeof listItem.current?.clientHeight !== 'undefined' ? listItem.current?.clientHeight : 0
    const elOffset: number = elHeight * activeIndex

    if (list.current) list.current.scrollTop = elOffset
  }

  const [scrollOff, setScrollOff] = useState(false)

  useEffect(() => {
    if (selectedItem > 0) {
      setSelectedItem(0)
    }
  }, [searchString])

  useEffect(() => {
    setSelectedItem(activeIndex)

    if (!scrollOff) scrollToTop()
  }, [activeIndex])

  if (!props.open) {
    return null
  }

  return (
    <Wrapper>
      {searchResult.length > 0 && (
        <>
          <List ref={list}>
            {searchResult.map((value, index) => {
              return (
                <li ref={listItem} key={index}>
                  <SearchResultItem
                    key={index}
                    isActive={selectedItem === index}
                    onSelectTemplate={onSelectTemplate}
                    onMouseEnter={() => {
                      setScrollOff(true)
                      if (typeof onItemHover !== 'undefined') onItemHover(index)
                    }}
                    onMouseLeave={() => {
                      setScrollOff(false)
                      if (typeof onItemHover !== 'undefined') onItemHover(index)
                    }}
                    onFocus={() => {
                      if (typeof onItemHover !== 'undefined') onItemHover(index)
                    }}
                    {...value}
                  />
                </li>
              )
            })}
          </List>

          {searchResult[selectedItem] && (
            <Preview
              href={searchResult[selectedItem].href}
              title={searchResult[selectedItem].title}
              onClick={e => {
                if (onSelectTemplate) {
                  e.preventDefault()
                  onSelectTemplate(searchResult[selectedItem])
                }
              }}
            >
              <Box
                $styles={{
                  position: 'relative',
                  width: 96,
                  height: 62,
                  marginBottom: 22,
                }}
              >
                <LazyImage
                  lazyload={false}
                  detectIntersection={false}
                  width={96}
                  height={62}
                  name={searchResult[selectedItem].title}
                  original={{
                    '*': searchResult[selectedItem].preview,
                  }}
                />
              </Box>
              <Text as="div" $variant="p1medium" $mb={8}>
                {searchResult[selectedItem].title}
              </Text>
              <Text $variant="p2">{searchResult[selectedItem].description}</Text>
            </Preview>
          )}
        </>
      )}
      {searchResult.length === 0 && isSearching && <NoResult>{statusMessages.SEARCHING}</NoResult>}
      {!isSearching && searchString.length >= 3 && searchResult.length == 0 && (
        <NoResult>{statusMessages.NO_RESULT}</NoResult>
      )}
      {!isSearching && searchString.length < 3 && searchResult.length == 0 && (
        <NoResult>{statusMessages.TYPE_MORE}</NoResult>
      )}
    </Wrapper>
  )
}

export default SearchDropdown
